export {}; 

// --- USER & AUTH ---
export interface UserProfile {
  username: string;
  password: string;
  joinedAt: number;
}

// --- TRADE DATA ---
export interface Lot {
  contracts: number;
  premium: number;
  date: number;
}

export interface Exit {
  id: string;
  contracts: number;
  price: number;
  date: number;
}

export interface Trade {
  id: string;
  user: string;
  startDate: number;
  ticker: string;
  type: 'CALL' | 'PUT';
  expiration: string;
  strike: string;
  lots: Lot[];
  exits: Exit[];
  status: 'OPEN' | 'CLOSED';
}

// --- MATH HELPERS ---
export const getTotals = (trade: Trade) => {
  const totalBought = trade.lots.reduce((acc, l) => acc + l.contracts, 0);
  const totalSold = trade.exits.reduce((acc, e) => acc + e.contracts, 0);
  const remaining = totalBought - totalSold;

  let totalBuyCost = 0;
  trade.lots.forEach(l => totalBuyCost += (l.contracts * l.premium * 100));
  const avgEntry = totalBought > 0 ? (totalBuyCost / totalBought) / 100 : 0;

  let realizedPnL = 0;
  trade.exits.forEach(e => {
    realizedPnL += (e.price - avgEntry) * e.contracts * 100;
  });

  const totalSoldCash = trade.exits.reduce((acc, e) => acc + (e.contracts * e.price * 100), 0);
  const remainingCost = Math.max(0, totalBuyCost - totalSoldCash);

  // ROI % Calculation
  const pnlPct = totalBuyCost > 0 ? (realizedPnL / totalBuyCost) * 100 : 0;

  return { totalBought, totalSold, remaining, avgEntry, realizedPnL, remainingCost, totalBuyCost, pnlPct };
};

// --- STOP LOSS CALCULATOR (70% LOSS) ---
// This is the function the error says is missing
export const calculateStopLoss = (avgFill: number): number => {
  const retainPct = 0.30; // Retain 30% (Lose 70%)
  const rawPrice = avgFill * retainPct;
  return Math.ceil(rawPrice * 100) / 100; 
};

// --- STATS GENERATOR ---
export const getStats = (trades: Trade[]) => {
  const now = new Date();
  const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const today = new Date();
  const day = today.getDay(); 
  const diff = today.getDate() - day; 
  const startOfWeek = new Date(today.setDate(diff)).setHours(0,0,0,0);
  const startOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).getTime();
  const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();

  let dayPnL = 0, weekPnL = 0, monthPnL = 0, yearPnL = 0, allTimePnL = 0;

  trades.forEach(t => {
    const stats = getTotals(t);
    if (stats.realizedPnL === 0) return;
    allTimePnL += stats.realizedPnL;
    if (t.startDate >= startOfDay) dayPnL += stats.realizedPnL;
    if (t.startDate >= startOfWeek) weekPnL += stats.realizedPnL;
    if (t.startDate >= startOfMonth) monthPnL += stats.realizedPnL;
    if (t.startDate >= startOfYear) yearPnL += stats.realizedPnL;
  });

  return { dayPnL, weekPnL, monthPnL, yearPnL, allTimePnL };
};

// --- LEVELING SYSTEM ---
const RANKS = ["ROOKIE RUNNER", "SCALPER", "TRADER", "PRO", "ELITE", "LEGEND", "WHALE", "MARKET MAKER"];
export interface LevelInfo {
  currentLevel: number;
  currentGoal: number;
  progressInLevel: number;
  totalRunners: number;
  rankTitle: string;
  nextRankTitle: string;
}

export const getLevelInfo = (totalRunners: number): LevelInfo => {
  let rankIndex = 0, currentGoal = 5, progressInLevel = totalRunners;
  if (totalRunners >= 5) {
    const excess = totalRunners - 5;
    rankIndex = 1 + Math.floor(excess / 10);
    if (rankIndex >= RANKS.length) rankIndex = RANKS.length - 1;
    currentGoal = 10;
    progressInLevel = excess % 10;
  }
  return { 
    currentLevel: rankIndex + 1, currentGoal, progressInLevel, totalRunners, 
    rankTitle: RANKS[rankIndex], nextRankTitle: RANKS[Math.min(rankIndex + 1, RANKS.length - 1)] 
  };
};

export const getScenarios = (remainingContracts: number, remainingCost: number, avgEntry: number, currentBid?: number) => {
  const scenarios = [];
  if (remainingContracts <= 0 || remainingCost <= 0) return [];
  for (let x = 1; x < remainingContracts; x++) {
    const sellQty = remainingContracts - x;
    const requiredShare = Math.ceil((remainingCost / sellQty) / 100 * 100) / 100;
    if (!isFinite(requiredShare)) continue;
    const baseForGain = currentBid && currentBid > 0 ? currentBid : avgEntry;
    const gainNeededPct = Math.round(((requiredShare / baseForGain) - 1) * 100);
    scenarios.push({ freeGoal: x, sellQty, limitPriceShare: requiredShare, limitPriceContract: requiredShare * 100, gainNeededPct });
  }
  return scenarios;
};